import {
    Component,
    EventEmitter,
    Output,
    OnDestroy,
    Input,
    OnInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    output,
    OutputEmitterRef,
    input,
    InputSignal,
    effect,
    InputSignalWithTransform,
    WritableSignal,
    signal,
    Signal,
    computed
} from '@angular/core'
import moment from 'moment'
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms'
import { ToastHelper } from '@app/core/services/toast.service'
import { markControlsAsTouched } from '@app/shared/utils/markControlsAsTouched.util'
import { Expense, FOLLOW_UP_RESOURCE, Trip, User } from '@app/shared/models'
import * as fromModalStore from '@app/modals/store'
import * as fromCoreStore from '@app/core/store'
import * as fromShopStore from '@app/shop/store'
import { Store } from '@ngrx/store'

import { OpenModal, CloseModal } from '@app/modals/store/actions/layout.actions'
import { MULTI_SELECT_PRESET_VALUE_CONFIG, } from '@app/shared/buttons/select.config'
import { multiselectGetValues, RedirectService } from '@app/shared'
import { Router } from '@angular/router'
import { AuthService } from '@app/users/services'
import { popupService } from '@app/shared/services/popup.service'
import { Subject, take, takeUntil } from 'rxjs'
import { AccountChart } from '@app/shared/models/account-chart.model'
import { toSignal } from '@angular/core/rxjs-interop'
import { ExpensesService } from '@app/core/services'

/** Checks if it may be object id, but still needs validation on backend */
function isValidObjectId(id: string): boolean {
  return /^[a-f\d]{24}$/i.test(id);
}

@Component({
    selector: 'app-expense-details',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './expense-details.component.html',
    styleUrls: ['./expense-details.component.scss'],
    standalone: false
})

export class ExpenseDetailsComponent implements OnDestroy, OnInit {

    public readonly onSave: OutputEmitterRef<any> = output()
    public readonly onChange: OutputEmitterRef<any> = output()
    public readonly onDelete: OutputEmitterRef<any> = output()

    public readonly currentUser: InputSignal<User> = input()
    public readonly users: InputSignal<User[]> = input([])

    public readonly accountsCharts: InputSignal<{ parentId: string, accounts: AccountChart[] }[]> = input.required({
        transform: (value: { parentId: string, accounts: AccountChart[] }[] | null) => {
            if(!value?.length) return []

            if (this.expenseForm?.value?.status === 'NEW' && value?.length) {
                for (let v of value) {
                    if (v?.accounts?.length) {
                        v.accounts = v.accounts.filter(a => a?.isActive || (a?._id == this.expenseForm.value?.accountChart))
                    }
                }
            }
             return value.map(g => {
                g.accounts.filter(a => a?.name).map((a) => {
                    a._name = a?._contact?.name ? `${a.name}, ${a?._contact?.name}` : a.name
                    return a
                })
                return g
            })
        }
    })

    public readonly isReadOnly: InputSignal<boolean> = input()

    public readonly expense: InputSignal<Expense> = input.required({
        transform: (val: Expense | null) => {
            if(val && !val?.amounts?.length){
                val.amounts = [{ value: 0, resource: '', resourceId: '' }]
            }
            return val
        }
    })

    public readonly expenseForm: UntypedFormGroup

    public readonly categoriesArray: InputSignalWithTransform<
        { key: string; name: string }[],
        { [key: string]: string } | null
    > = input.required({
        transform: (val: { [key: string]: string } | null) => {
            if(!val) return null
            return Object.keys(val).map(key => {
                return { key: key, name: val[key] }
            })
        },
        alias: 'categories'
    })

    public String = String
    public userIndex: string = '0'
    public readonly FOLLOW_UP_RESOURCE = FOLLOW_UP_RESOURCE

    /** @deprecated - Check if it's being used and remove if not */
    private isSelected: Boolean = false
    /** @deprecated - Check if it's being used and remove if not */
    private expDisabled: boolean

    public readonly statuses = [
        { name: 'NEW', key: 'NEW' },
        { name: 'IN REVIEW', key: 'IN_REVIEW' },
        { name: 'ACCEPTED', key: 'ACCEPTED' },
        { name: 'DENIED', key: 'DENIED' }
    ]
    public readonly tags = [
        {
            value: 'WARNING', label: 'WARNING'
        },
        {
            value: 'REFUND', label: 'REFUND'
        },
        {
            value: 'MISSING_RECEIPTS', label: 'MISSING RECEIPTS'
        }
    ]

    readonly defaultResources = [
        { name: FOLLOW_UP_RESOURCE.TRIP, key: FOLLOW_UP_RESOURCE.TRIP },
        { name: FOLLOW_UP_RESOURCE.ORDER, key: FOLLOW_UP_RESOURCE.ORDER },
        { name: FOLLOW_UP_RESOURCE.CLAIM, key: FOLLOW_UP_RESOURCE.CLAIM },
        { name: FOLLOW_UP_RESOURCE.ADS, key: FOLLOW_UP_RESOURCE.ADS }
    ]
    public readonly resources: Signal<this['defaultResources']> = computed(() => this.isAdsExpense() ? [{ name: FOLLOW_UP_RESOURCE.ADS, key: FOLLOW_UP_RESOURCE.ADS }] : this.defaultResources)

    public readonly paymentTypes = [
        {key: 'ach', name:'ACH / Zelle / Venmo'},
        {key: 'companyCard', name:'Company Card'},
        {key: 'cash', name:'COP/COD Cash'},
        {key: 'reimburse', name:'Driver Reimburse'},
        {key: 'check', name:'Check'}
    ]

    private readonly destroyed$: Subject<boolean> = new Subject<boolean>()
    public readonly selectedTab: WritableSignal<'notes' | 'tasks'> = signal('notes')
    public sources: Signal<any[]> = null
    public readonly isAdsExpense: WritableSignal<boolean> = signal(false)
    public submitAttempted = false;
    public missingResourceLinkIdx: number[] = [];

    constructor(
        private store: Store<fromCoreStore.State>,
        private modalStore: Store<fromModalStore.State>,
        private fb: UntypedFormBuilder,
        private notify: ToastHelper,
        private popupService: popupService,
        private _Router: Router,
        private cdr: ChangeDetectorRef,
        private redirectService: RedirectService,
        private expensesService: ExpensesService
    ) {
        this.userIndex = this.redirectService.getUrlIndex()
        this.expenseForm = this.fb.group({
            _id: [''],
            status: ['', Validators.required],
            expenseDate: [''],
            checkNo: [''],
            merchant: [''],
            category: ['', Validators.required],
            amounts: [[], Validators.required],
            reimbursement: [false],
            closed: [false],
            txnDate: [''],
            paymentType: [''],
            desc: [null],
            employeeId: [''],
            employeeName: [''],
            vendorId: [''],
            vendorName: [''],
            accountChart: [null],
            tags: [[]],
            fuelGallons: [null]
        })

        effect(() => {
            let expense = this.expense()
            if(expense){
                if (this.expenseForm.value._id) {
                    return
                }
                this.expenseForm.patchValue(expense)
            }
        })
        // this.expenseForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value) => {
        //     if(value?.amounts?.length && value?.amounts?.some(a => a.resoure === FOLLOW_UP_RESOURCE.ADS)){
        //         this.expenseForm.get('category').setValidators()
        //     }
        // })
        this.sources = toSignal(this.store.select(fromCoreStore.selectCoreValSources), { initialValue: []})
    }
    ngOnInit() {
        this.expenseForm.get('closed').valueChanges
            .subscribe(value => {
                const txnDateControl = this.expenseForm.get('txnDate')
                if (value) {
                    txnDateControl.setValidators(Validators.required)
                } else {
                    txnDateControl.clearValidators()
                }
                txnDateControl.updateValueAndValidity()
            })
        this.expenseForm.get('amounts').valueChanges
            .subscribe((value: Expense['amounts']) => {
                // If all resources are ADS we don't need to set category
                if(value?.length && value?.every(a => a.resource === FOLLOW_UP_RESOURCE.ADS ? true : (value?.some(a => a.resource === FOLLOW_UP_RESOURCE.ADS) && !a.resourceId))){
                    this.expenseForm.get('category').clearValidators()
                    this.isAdsExpense.set(true)
                }else{
                    this.expenseForm.get('category').setValidators(Validators.required)
                    this.isAdsExpense.set(false)
                }
                this.expenseForm.get('category').updateValueAndValidity()
            })
    }
    setTab(event: 'notes' | 'tasks'): void {
        this.selectedTab.set(event)
    }
    addAmount(): void {
        this.submitAttempted = false;
        let array = this.expenseForm.get('amounts').value
        array.push({ value: 0, resource: this.isAdsExpense() ? FOLLOW_UP_RESOURCE.ADS : '', resourceId: '' })
        this.expenseForm.get('amounts').setValue(array)
    }
    updateAmount(index: number, changes: Partial<Expense['amounts']['0']>): void {
      const array = this.expenseForm.get('amounts').value;
      const currentAmount = array[index];
      const updatedAmount = { ...currentAmount, ...changes };

      // Check deduction amount if deductValue is being updated
      if ('value' in changes && (currentAmount.value != changes.value)) {
        const amountToCheck = changes.deductValue || changes.value;
        const amountToCheckNum = typeof amountToCheck === 'string' ? parseFloat(amountToCheck) : amountToCheck;

        // If amount is cleared (empty or 0), permanently accept and clear warning
        if (!amountToCheck || amountToCheckNum === 0) {
          delete updatedAmount._warning;
          delete updatedAmount._expenseNumbers;
        } else {

          // Call service to check amount
          this.expensesService.checkAmount(amountToCheckNum, this.expense()?._id).pipe(take(1)).subscribe({
            next: (resp) => {
              const array = this.expenseForm.get('amounts').value;
              const amount = array[index];

              if (resp.data.exists) {
                // Amount already exists, add warning
                amount._warning = true;
                amount._expenseNumbers = resp.data.expenseNumbers;
              } else {
                // Amount is valid, remove warning
                delete amount._warning;
                delete amount._expenseNumbers;
              }

              array[index] = amount;
              this.expenseForm.get('amounts').setValue(array);
              this.cdr.markForCheck();
            },
            error: (error) => {
              // On error, don't add warning but log it
              console.error('Error checking amount:', error);
            }
          });
        }
      }

      array[index] = updatedAmount;
      this.expenseForm.get('amounts').setValue(array);
      this.cdr.markForCheck();
    }

    removeAmount(index: number): void {
      const array = this.expenseForm.get('amounts').value;
      array.splice(index, 1);
      this.expenseForm.get('amounts').setValue(array);
      this.cdr.markForCheck();
    }

    onFuelGallonsChange(val): void {
      const gallons = Number(val) || 0
      this.expenseForm.get('fuelGallons').setValue(gallons)
      this.cdr.markForCheck()
    }
    checkValidate(): void {
        this.expenseForm.invalid
            ? this.expDisabled = true
            : this.expDisabled = false
    }

    setValue(value: any, key): void {
        this.expenseForm.get(key).setValue(value)
    }

    setVendor($event): void {
        this.expenseForm.get('vendorId').setValue($event._id)
        this.expenseForm.get('vendorName').setValue($event.name)
    }
    clearVendor(): void {
        this.expenseForm.get('vendorId').setValue('')
        this.expenseForm.get('vendorName').setValue('')
    }

    setEmployee($event): void {
        this.expenseForm.get('employeeId').setValue($event._id)
        this.expenseForm.get('employeeName').setValue($event.name)

        // Broken logic, didn't work long time
        // if (this.accountsCharts.length) {
        //     this.accountsCharts.map(ac => {
        //         if (ac.contactId === $event._id) {
        //             this.expenseForm.get('accountChart').setValue(ac._id)
        //         }
        //     })
        // }
    }
    clearEmployee(): void {
        this.expenseForm.get('employeeId').setValue('')
        this.expenseForm.get('employeeName').setValue('')
    }

    setAccountChart($event): void {
        this.expenseForm.get('accountChart').setValue($event || null)
    }

    onDateChange($event, key): void {
        const date = $event[key] ? moment($event[key].substring(0, $event[key].length - 1)).format('YYYY-MM-DD') : ''
        this.expenseForm.get(key).setValue(date)
    }

    updateSource(index: number, source: { key: string, name: string }): void {
        const changes = { resourceName: source?.name, resourceId: isValidObjectId(source?.key) ? source?.key : null }
        this.updateAmount(index, changes)
    }


    saveChange(): void {
        const data = {
            ...this.expenseForm.value
        }
        this.onChange.emit(data)
    }
    isRowValid(a: any): boolean {
      if (!a?.resource) return true;
      if (a.resource === FOLLOW_UP_RESOURCE.ADS) return !!a?.resourceName;
      return !!a?.resourceId;
    }
    save(): void {
        this.submitAttempted = true;

        const amounts = this.expenseForm.get('amounts')?.value || [];
        const invalid = amounts.some(a => !this.isRowValid(a));

        if (invalid) {
          this.notify.error('Please select resource item!');
          this.cdr.markForCheck();
          return;
        }

        if (this.expenseForm.invalid || this.isSelected) {
            const formValidStatus = markControlsAsTouched(this.expenseForm)
            if (!formValidStatus || this.isSelected) {
                this.notify.error('Please enter valid form inputs!')
                return
            }
        }
        const data:{[key: string]: any} = {
            ...this.expenseForm.value,
            employee: this.expenseForm?.value?.employeeId && this.expenseForm?.value?.employeeId !== ''
                ?
                {
                    employeeId: this.expenseForm?.value?.employeeId,
                    name: this.expenseForm?.value?.employeeName
                }
                :
                {
                    employeeId: '',
                    name: '',
                },
            vendor: this.expenseForm?.value?.vendorId && this.expenseForm?.value?.vendorId !== ''
                ?
                {
                    _id: this.expenseForm?.value?.vendorId,
                    name: this.expenseForm?.value?.vendorName
                }
                :
                {
                    _id: null,
                    name: null,
                },
        }
        delete data.employeeName
        delete data.employeeId
        delete data.vendorName
        delete data.vendorId
        // If we remove account chart, we have to provide null, not empty string
        if(data.hasOwnProperty('accountChart')) {
            data.accountChart = data.accountChart || null
        }

        // Remove _warning property from amounts before saving
        if (data.amounts && Array.isArray(data.amounts)) {
            data.amounts = data.amounts.map(amount => {
                const { _warning, _expenseNumbers, ...amountWithoutWarning } = amount;
                return amountWithoutWarning;
            });
        }

        this.onSave.emit(data)
    }
    restore(): void {
        this.store.dispatch(
            new fromCoreStore.RestoreExpense(this.expense())
        )
        this.store.dispatch(
            new CloseModal()
        )
    }

    toggleResources = (resource: string): void => {
        this.expenseForm.get('resource').setValue(resource)
        this.expenseForm.get('resourceId').setValue('')
    }

    openResource(resource: keyof typeof FOLLOW_UP_RESOURCE, resourceId: string): void {
        switch(resource) {
            case FOLLOW_UP_RESOURCE.TRIP: {
                const params = {
                    report: 'byTrip',
                    startDate: moment(new Date('08/01/17')).startOf('month').format('MM/DD/YY'),
                    endDate: moment().startOf('month').format('MM/DD/YY'),
                    status: 'ACTIVE',
                    ordersType: 'ALL',
                    tripsIds: resourceId
                }
                const url = this._Router.serializeUrl(this._Router.createUrlTree([`/${this.userIndex}/reports`], { queryParams: { ...params } }))
                let baseUrl = window.location.href.replace(this._Router.url, '')
                window.open(baseUrl + url, '_blank')
                return
            }
            case FOLLOW_UP_RESOURCE.ORDER: {
                const url = this._Router.serializeUrl(this._Router.createUrlTree([`/${this.userIndex}/loadboard/${resourceId}`]))
                let baseUrl = window.location.href.replace(this._Router.url, '')
                window.open(baseUrl + url, '_blank')
                return
            }
            case FOLLOW_UP_RESOURCE.CLAIM: {
                const url = this._Router.serializeUrl(this._Router.createUrlTree([`/${this.userIndex}/claims/${resourceId}`]))
                let baseUrl = window.location.href.replace(this._Router.url, '')
                window.open(baseUrl + url, '_blank')
                return
            }
            case FOLLOW_UP_RESOURCE.ADS: {
                this.store.dispatch(
                    new OpenModal({
                        type: 'EditContact',
                        props: { contactId: resourceId }
                    })
                )
                return
            }
        }
    }

    openInReport(tripId: string): void {
        const params = {
            report: 'byTrip',
            startDate: moment(new Date('08/01/17')).startOf('month').format('MM/DD/YY'),
            endDate: moment().startOf('month').format('MM/DD/YY'),
            status: 'ACTIVE',
            ordersType: 'ALL',
            tripsIds: tripId
        }
        const url = this._Router.serializeUrl(this._Router.createUrlTree([`/${this.userIndex}/reports`], { queryParams: { ...params } }))
        let baseUrl = window.location.href.replace(this._Router.url, '')
        window.open(baseUrl + url, '_blank')
    }

    openInTrip(tripId: string): void {
        const url = this._Router.serializeUrl(this._Router.createUrlTree([`/${this.userIndex}/trips`, tripId]))
        let baseUrl = window.location.href.replace(this._Router.url, '')
        window.open(baseUrl + url, '_blank')
    }


    openContactModal(contactId: string): void {
        this.store.dispatch(
            new OpenModal({
                type: 'EditContact',
                props: { contactId }
            })
        )
    }

    openEmployeeModal(contactId: string): void {
        this.store.dispatch(
            new OpenModal({
                type: 'UPDATE_CONTACT',
                props: { contactId, windowClass: 'contact-modal' }
            })
        )
    }

    async setTrip(i: number, trip: Trip): Promise<void> {
        if (trip?.driverPaid || trip?.driverConfirmation?.date) {
            if (await this.popupService.confirm(`Trip invoice has been ${trip?.driverPaid ? 'Paid' : 'Approved by the Driver'} still want to link expense?`) === false) {
                return
            }
        }

        const changes = { resourceId: trip._id, resourceName: trip.tripName }
        this.updateAmount(i, changes)
        this.cdr.detectChanges()
    }
    setOrder(i: number, event: any): void {
        const changes = { resourceId: event?.item?._id, resourceName: event?.item?.quoteNumber }
        this.updateAmount(i, changes)
    }
    setClaim(i: number, event: any): void {
      const changes = { resourceId: event?.claimId, resourceName: event?.claimName };
      this.updateAmount(i, changes);
    }

    getTotalAmount(): number {
        let total = 0
        this.expenseForm.get('amounts').value.map(amount => {
            total += +amount.value
        })
        console.log(total)
        return total
    }

    uploadPhotos() {
        this.store.dispatch(new fromCoreStore.GetFiles({ resource: 'EXPENSE', resourceId: this.expense()?._id }))
    }

    hasAccessToTrip = trip => trip?.creator?.companyId === AuthService.CURRENT_USER?.company?._id && (!this.currentUser()?.permissions?.contact_roles_driver || trip?.driverIds?.includes(this.currentUser()?._id))

    getStatusName(key) {
        return this.statuses.find(s => s.key === key)?.name
    }

    createOrder() {
        this.store.dispatch(new fromShopStore.CreateShopOrderFromExpense({ expenseId: this.expense()._id }))
    }

    ngOnDestroy() {
        this.destroyed$.next(true)
        this.destroyed$.complete()
    }
}
